import React, { useCallback, useState, useEffect, useMemo, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnnotationsDispatch } from '@confluence/annotation-provider-store';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { type DialogsStateContainer, withDialogs } from '@confluence/dialogs';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { getNextUnreadThreadKey, openInlineCommentInEditor } from '@confluence/comments-util';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks';
import {
	useObjectCommentDispatchContext,
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context';
import {
	CommentType,
	useCommentsDataState,
	useActiveCommentsQuery,
} from '@confluence/comments-data';
import { useCreateCommentAction } from '@confluence/highlight-actions';
import { useInlineCommentQueryParams } from '@confluence/comment';
import {
	GeneralShortcutListener,
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT,
	UNIVERSAL_OPEN_COMMENT_EDITOR,
} from '@confluence/shortcuts';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel';
import { useCommentsPanel, useCommentsPanelScroll } from '@confluence/comments-panel-utils';
import { useObjectSidebarState, PanelName } from '@confluence/object-sidebar-api';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';
import {
	VIEW_INLINE_COMMENT_EXPERIENCE,
	ExperienceTrackerContext,
	ExperienceTimeout,
} from '@confluence/experience-tracker';
import { usePageClass } from '@confluence/comments-hooks';

import { parsePanelAndInlineRefs, getNextRef } from './helper/commentsShortcutsHelper';
import { useFooterComments } from './useFooterComments';

const EditorCommentShortcutListenerComponent = ({
	contentId,
	viewMode,
	dialogs,
	editorApi,
}: {
	contentId?: string;
	viewMode?: boolean;
	dialogs: DialogsStateContainer;
	editorApi?: FullPageEditorPresetAPI;
}) => {
	const { navigateToInlineComment, setActiveHighlight } = useInlineCommentsDispatchContext();
	const { unresolvedInlineComments } = useInlineCommentsState();
	const { orderedActiveAnnotationIdList, commentsDataMap } = useCommentsDataState();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activeHighlight } = useInlineCommentsContext();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);
	const [alreadyFetchedInitialData, setAlreadyFetchedInitialData] = useState(false);
	const [triggeredByUnreadKeyboardShortcut, setTriggeredByUnreadKeyboardShortcut] = useState(false);
	const [
		{ currentlyRenderedThreads, currentlySelectedThreadKey },
		{ setCurrentlySelectedThreadKey },
	] = useCommentsPanel();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isObjectSidebarShown, panel } = useObjectSidebarState();
	const pageMode = useGetPageMode();
	const emitter = getEditorAnnotationEventEmitter();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageClass = usePageClass();

	const { expandCommentContainer } = useObjectCommentDispatchContext();

	const orderedActiveAnnotations = useMemo(() => {
		return fg('confluence_frontend_new_comments_scrolling')
			? orderedActiveAnnotationIdList.map((a) => a.threadKey)
			: unresolvedInlineComments;
	}, [orderedActiveAnnotationIdList, unresolvedInlineComments]);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight, currentlySelectedThreadKey]);

	useEffect(() => {
		return () => {
			setCurrentlySelectedThreadKey('');
		};
	}, [setCurrentlySelectedThreadKey]);

	const { footerCommentThreads, footerCommentIds } = useFooterComments();
	const { setEditorViewProps } = useAnnotationsDispatch();

	const { unreadInlineComments, unreadGeneralComments } = useMemo(() => {
		const unreadInlineComments: string[] = [];
		const unreadGeneralComments: string[] = [];

		orderedActiveAnnotations.forEach((threadKey) => {
			if (
				commentsDataMap.inline[threadKey]?.isUnread ||
				commentsDataMap.inline[threadKey]?.replies?.some((reply) => reply.isUnread)
			) {
				unreadInlineComments.push(threadKey);
			}
		});
		Object.keys(commentsDataMap.general).forEach((threadKey) => {
			if (
				commentsDataMap.general[threadKey]?.isUnread ||
				commentsDataMap.general[threadKey]?.replies?.some((reply) => reply.isUnread)
			) {
				unreadGeneralComments.push(threadKey);
			}
		});

		return {
			unreadInlineComments,
			unreadGeneralComments,
		};
	}, [commentsDataMap, orderedActiveAnnotations]);

	const sendKeyboardShortcutAnalytics = (shortcut: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'editPage',
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
				actionSubjectId: 'createComment',
				attributes: {
					key: shortcut,
				},
			},
		}).fire();
	};

	const { handleCommentButtonClick } = useCreateCommentAction({
		contentId: contentId || '',
		isFabric: true,
		sendKeyboardShortcutAnalytics,
	});

	const onNavigationClick = useCallback(
		(nextMarkerRef?: string) => {
			setCurrentlySelectedThreadKey('');
			if (!nextMarkerRef) return;

			emitter.emit('setselectedannotation', nextMarkerRef);
			const commentElement = document.getElementById(nextMarkerRef);
			if (commentElement) {
				/*
				 * Don't start the experience if the create sidebar is shown because we can't predict
				 * if the user will close the create sidebar to continue the navigation or create a
				 * new comment. If the user continues the navigation, the experience will be started
				 * by the annotation-provider-inline-comments/editor/ViewManager entrypoint.
				 */
				if (
					document.querySelector('[data-testid="editor-create-sidebar"]') === null &&
					(pageClass === 'renderer' || pageClass === 'editor') &&
					fg('confluence_frontend_view_inline_comment_keyboard')
				) {
					experienceTracker.start({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
						attributes: {
							annotationId: nextMarkerRef,
							entryPoint: 'comments-shortcuts/EditorCommentShortcutListener',
							pageClass: 'editor',
						},
					});
				}
				openInlineCommentInEditor(commentElement, nextMarkerRef);
			}
		},
		[emitter, setCurrentlySelectedThreadKey, experienceTracker, pageClass],
	);

	const scrollToFooterComment = useCallback(
		(threadKey: string) => {
			// Close inline comment when keyboard navigating from inline comment to footer comment
			// TODO: The logic to close inline comment is from ViewManager.tsx. To revisit - create a shared function so the close inline comment logic can be reused
			if (fg('confluence_frontend_editor_annotation_a11y_fix')) {
				setTimeout(() => {
					editorApi?.core.actions.focus();
				}, 100);
			}
			emitter.emit('closeinlinecomment');
			setEditorViewProps(null);

			setCurrentlySelectedThreadKey(threadKey);
			handlePanelSelectionAndScroll({
				threadKey,
				isResolved: false,
				pageMode,
				setCurrentlySelectedThreadKey,
				eventEmitter: emitter,
				commentType: CommentType.GENERAL,
			});
			setActiveHighlight(threadKey);
		},
		[
			emitter,
			pageMode,
			editorApi,
			setCurrentlySelectedThreadKey,
			setActiveHighlight,
			setEditorViewProps,
		],
	);

	const getCommentIndex = useCallback(
		(currentMarkerRef: string): number => {
			return fg('confluence_frontend_footer_kb_nav')
				? [...orderedActiveAnnotations, ...footerCommentIds]?.findIndex(
						(markerRef) => markerRef === currentMarkerRef,
					)
				: orderedActiveAnnotations?.findIndex((markerRef) => markerRef === currentMarkerRef);
		},
		[orderedActiveAnnotations, footerCommentIds],
	);

	const handlePanelCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// If nothing is on the panel, there's nothing to navigate to
			if (currentlyRenderedThreads.length === 0) {
				return;
			}

			const { panelCommentRefs, navigableInlineCommentRefs } = parsePanelAndInlineRefs(
				currentlyRenderedThreads,
				orderedActiveAnnotations,
			);

			const nextRef = getNextRef(action, panelCommentRefs, currentlySelectedThreadKey);

			// Equivalent to clicking on the comment in the panel
			handlePanelSelectionAndScroll({
				threadKey: nextRef.ref,
				isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
				pageMode,
				setCurrentlySelectedThreadKey,
				eventEmitter: emitter,
				commentType: nextRef.type,
				scrollCommentsPanelToThread,
				shouldScrollCommentsPanel: true,
				hasKeyPressed: true,
			});
		},
		[
			currentlyRenderedThreads,
			orderedActiveAnnotations,
			currentlySelectedThreadKey,
			pageMode,
			setCurrentlySelectedThreadKey,
			emitter,
			scrollCommentsPanelToThread,
		],
	);

	const handleNextCommentNavigation = useCallback(
		({
			isNavigateToNextUnread,
			action,
		}: {
			isNavigateToNextUnread: boolean;
			action: 'next' | 'previous';
		}) => {
			//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
			if (isNextCommentLoading) return;

			setIsNextCommentLoading(true);

			navigateToInlineComment({
				action,
				triggeredByKeyboardShortcut: true,
				currentCommentIndex: getCommentIndex(activeHighlight || currentlySelectedThreadKey || ''),
				getCommentIndex,
				onNavigationClick,
				isEditor: true,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments,
				unreadGeneralComments,
				footerComments: footerCommentIds,
				scrollToFooterComment,
				commentsDataMap,
			});
		},
		[
			activeHighlight,
			navigateToInlineComment,
			onNavigationClick,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			createAnalyticsEvent,
			contentId,
			getCommentIndex,
			isNextCommentLoading,
			footerCommentIds,
			scrollToFooterComment,
			commentsDataMap,
			currentlySelectedThreadKey,
			unreadGeneralComments,
			unreadInlineComments,
		],
	);

	const handleStandaloneCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			const firstInlineCommentMarkerRef = orderedActiveAnnotations[0];

			if (activeHighlight || currentlySelectedThreadKey) {
				handleNextCommentNavigation({
					isNavigateToNextUnread: false,
					action,
				});
			} else {
				if (firstInlineCommentMarkerRef) {
					onNavigationClick(firstInlineCommentMarkerRef);
				} else if (pageMode === PageMode.LIVE && fg('confluence_frontend_footer_kb_nav')) {
					// Only Live pages have footer comments which is navigable
					scrollToFooterComment(footerCommentThreads[0]?.id);
				}
			}
		},
		[
			activeHighlight,
			onNavigationClick,
			handleNextCommentNavigation,
			scrollToFooterComment,
			footerCommentThreads,
			pageMode,
			currentlySelectedThreadKey,
			orderedActiveAnnotations,
		],
	);

	const handleCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// Define behavior when the comments panel is shown - panel FG is a prereq for nav, so has to be checked first
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('confluence-frontend-comments-panel') &&
				isObjectSidebarShown &&
				panel?.id === PanelName.CommentsPanel
			) {
				handlePanelCommentNavigation(action);
			} else {
				// Comments panel not shown
				handleStandaloneCommentNavigation(action);
			}
		},
		[
			isObjectSidebarShown,
			panel?.id,
			handlePanelCommentNavigation,
			handleStandaloneCommentNavigation,
		],
	);

	useActiveCommentsQuery({
		pageId: contentId || '',
		skip: !triggeredByUnreadKeyboardShortcut || alreadyFetchedInitialData,
		onCompletedFn: () => {
			setAlreadyFetchedInitialData(true);
		},
	});

	const handleNextUnreadNavigation = useCallback(() => {
		if (!alreadyFetchedInitialData) {
			//This will trigger the ActiveComments query if it's not already fetched
			setTriggeredByUnreadKeyboardShortcut(true);
			//Exit because the comment data won't be available yet to navigate to
			return;
		}

		if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
			// No unread comments - don't do anything
			return;
		}

		//unread panel navigation
		if (
			isObjectSidebarShown &&
			panel?.id === PanelName.CommentsPanel &&
			fg('confluence-frontend-comments-panel')
		) {
			let nextThreadKey = '';
			let nextCommentType = CommentType.GENERAL;
			if (!currentlySelectedThreadKey) {
				if (unreadInlineComments.length > 0) {
					nextThreadKey = unreadInlineComments[0];
					nextCommentType = CommentType.INLINE;
				} else {
					nextThreadKey = unreadGeneralComments[0];
				}
			} else {
				nextThreadKey =
					getNextUnreadThreadKey(
						getCommentIndex(currentlySelectedThreadKey ?? '') + 1, //Add one otherwise it might get stuck on the current comment because there are unread replies which are collapsed
						[...orderedActiveAnnotations, ...footerCommentIds],
						[...unreadInlineComments, ...unreadGeneralComments],
					) ?? '';
				if (unreadInlineComments.includes(nextThreadKey)) {
					nextCommentType = CommentType.INLINE;
				}
			}

			handlePanelSelectionAndScroll({
				threadKey: nextThreadKey,
				isResolved: false,
				pageMode,
				setCurrentlySelectedThreadKey,
				eventEmitter: emitter,
				commentType: nextCommentType,
				scrollCommentsPanelToThread,
				shouldScrollCommentsPanel: true,
			});
			return;
		}

		if (activeHighlight || currentlySelectedThreadKey) {
			handleNextCommentNavigation({
				isNavigateToNextUnread: true,
				action: 'next',
			});
		} else {
			if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
				return;
			}
			if (unreadInlineComments[0]) {
				onNavigationClick(unreadInlineComments[0]);
			} else {
				scrollToFooterComment(unreadGeneralComments[0]);
			}
		}
	}, [
		isObjectSidebarShown,
		panel?.id,
		handleNextCommentNavigation,
		onNavigationClick,
		activeHighlight,
		unreadInlineComments,
		unreadGeneralComments,
		currentlySelectedThreadKey,
		scrollToFooterComment,
		orderedActiveAnnotations,
		getCommentIndex,
		footerCommentIds,
		emitter,
		pageMode,
		setCurrentlySelectedThreadKey,
		scrollCommentsPanelToThread,
		alreadyFetchedInitialData,
	]);

	useEffect(() => {
		if (
			unreadInlineComments.length + unreadGeneralComments.length > 0 &&
			alreadyFetchedInitialData &&
			triggeredByUnreadKeyboardShortcut
		) {
			// This handles the case where user triggers unread keyboard shortcut when the comments data isn't fetched yet.
			// We need to wait for the data to be fetched before calling handleNextUnreadNavigation
			setTriggeredByUnreadKeyboardShortcut(false);

			// Putting this behind a timeout because need to wait for triggeredByUnreadKeyboardShortcut to be set to false otherwise got called multiple times
			setTimeout(() => {
				handleNextUnreadNavigation();
			}, 100);
		}
	}, [
		unreadInlineComments,
		unreadGeneralComments,
		handleNextUnreadNavigation,
		alreadyFetchedInitialData,
		triggeredByUnreadKeyboardShortcut,
		setTriggeredByUnreadKeyboardShortcut,
	]);

	const openCommentEditorShortcutCb = (e: KeyboardEvent, shortcut: string) => {
		e.preventDefault();
		if (
			document.querySelector(
				fg('confluence_frontend_comments_universal_shortcuts')
					? '[data-testid="ak-editor-annotation-toolbar-create-button"]'
					: '[data-testid="editor-floating-toolbar"]',
			)
		) {
			if (fg('confluence_frontend_comments_universal_shortcuts')) {
				handleCommentButtonClick({ dialogs, shortcut });
			}
		} else {
			document
				.querySelector(`[data-testid='object-comment-wrapper']`)
				?.scrollIntoView({ behavior: 'smooth' });
			expandCommentContainer();
		}

		sendKeyboardShortcutAnalytics(shortcut);
	};

	return (
		<div data-testId="comments-shortcut-listener">
			<GeneralShortcutListener
				accelerator={UNIVERSAL_PREV_COMMENT_SHORTCUT}
				deprecatedAccelerator={viewMode ? PREV_COMMENT_SHORTCUT : undefined}
				listener={() => handleCommentNavigation('previous')}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_COMMENT_SHORTCUT}
				deprecatedAccelerator={viewMode ? NEXT_COMMENT_SHORTCUT : undefined}
				listener={() => handleCommentNavigation('next')}
			/>
			{fg('confluence_frontend_comments_universal_shortcuts') && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT}
					listener={handleNextUnreadNavigation}
				/>
			)}
			{fg('confluence-frontend-redesigned-footer-comment') && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
					listener={(e) => openCommentEditorShortcutCb(e, UNIVERSAL_OPEN_COMMENT_EDITOR)}
				/>
			)}
		</div>
	);
};

export const EditorCommentShortcutListener = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(withDialogs(EditorCommentShortcutListenerComponent));
